import { validateToken } from '../clients/user_client';
import { toast } from 'react-toastify';
import { differenceInDays, differenceInMonths } from 'date-fns';
import { CURRENCY_SYMBOLS, EXPIRY_CATEGORIES } from './config';
import { logInfo } from './logger';
import { buildListOpenedLotPatch } from '../utils/openedLotCount';

const LOG_CONTEXT_CART_PERSISTENCE = 'cartPersistence';
const LOG_CONTEXT_SESSION = 'session';

/**
 * Initializes the application by loading saved user information from storage.
 *
 * This function is called when the application is loaded. It checks local storage and session storage for previous user information.
 * If found, it validates the saved authentication token. If the token is valid, it sets the user's information and other saved states.
 * If the token is not valid or expired, it informs the user and clears the state.
 * If no saved user information is found in local storage or session storage, it simply sets the app's loading state to false.
 *
 * @async
 * @param {function} setRememberMe - State setter function for the 'rememberMe' boolean state.
 * @param {function} setToken - State setter function for the 'token' string state.
 * @param {function} setUserDetails - State setter function for the 'userDetails' object state.
 * @param {function} setNotifications - State setter function for the 'notifications' array state.
 * @param {function} setIsSupplier - State setter function for the 'isSupplier' boolean state.
 * @param {function} setIsLoading - State setter function for the 'isLoading' boolean state.
 * @param {function} setCart - State setter function for the 'cart' array state.
 * @param {function} setNotificationsSeen - State setter for the notificationsSeen boolean
 * @returns {Promise<void>}
 * @throws Will throw an error if there's an issue with the validateToken() async function call.
 */
export const initializeApp = async (
  setRememberMe,
  setToken,
  setUserDetails,
  setNotifications,
  setIsSupplier,
  setIsLoading,
  setCart,
  setNotificationsSeen
) => {
  // Select appropriate storage type based on whether 'rememberMe' was set
  let storage;
  if (localStorage.getItem('rememberMe') === 'true') {
    storage = localStorage;
  } else if (sessionStorage.getItem('token')) {
    storage = sessionStorage;
  } else {
    // If there was no 'rememberMe' or valid session, finish initialization as there are no saved user details
    setIsLoading(false);
    return;
  }

  // Get saved user details and session data
  const savedToken =
    storage.getItem('token') === 'null' ? null : storage.getItem('token');
  const savedUserDetails = JSON.parse(storage.getItem('userDetails'));
  const savedNotifications =
    storage.getItem('notifications') === 'undefined'
      ? null
      : JSON.parse(storage.getItem('notifications'));
  const savedNotificationsSeen =
    storage.getItem('notificationsSeen') === 'true';
  const savedIsSupplier = storage.getItem('isSupplier') === 'true';
  const savedCart =
    JSON.parse(storage.getItem('cart')) === 'null'
      ? null
      : JSON.parse(storage.getItem('cart'));

  // If we have a saved token, validate it
  if (savedToken) {
    const response = await validateToken(savedToken);
    // If the token is invalid or was null, notify the user if it was a session and clear the token state
    if (!response || !response.success) {
      if (storage === sessionStorage && savedToken) {
        alert('Your session has expired. Please log in again.');
      }
      setToken(null);
    } else {
      // If the token is valid, restore the saved user details and session state.
      // validate_token returns the authoritative user_details, so a role an Admin
      // changed (or a field added after this session was stored) applies on reload.
      const freshUserDetails = response.userDetails;
      const restoredUserDetails = freshUserDetails
        ? { ...(savedUserDetails || {}), ...freshUserDetails }
        : savedUserDetails;

      setToken(savedToken);
      setUserDetails(restoredUserDetails);
      setNotifications(savedNotifications);
      setIsSupplier(savedIsSupplier);
      if (savedCart) setCart(savedCart);
      setNotificationsSeen(savedNotificationsSeen);

      if (freshUserDetails) {
        storage.setItem('userDetails', JSON.stringify(restoredUserDetails));
        logInfo(LOG_CONTEXT_SESSION, 'session user details refreshed', {
          userId: restoredUserDetails?.user_id ?? null,
          accountRole: restoredUserDetails?.account_role ?? null,
          isLabViewer: restoredUserDetails?.is_lab_viewer === true,
          storage: storage === localStorage ? 'localStorage' : 'sessionStorage',
        });
      }
    }
  }

  // Set the isLoading state to false, indicating that initialization has completed
  setIsLoading(false);
};

/**
 * Resolve which Web Storage API object holds the authoritative cart for the current session.
 * Matches the read path in {@link initializeApp}: remember-me uses localStorage; otherwise
 * sessionStorage when a session token exists.
 *
 * @returns {Storage} Active storage for cart reads/writes during the current login.
 */
export function getActiveCartStorage() {
  if (localStorage.getItem('rememberMe') === 'true') {
    return localStorage;
  }
  if (sessionStorage.getItem('token')) {
    return sessionStorage;
  }
  return localStorage;
}

/**
 * Persist the shopping cart to browser storage so it survives refresh and matches init restore.
 * Writes to the active storage (session vs remember-me) and always mirrors to localStorage.
 *
 * @param {Array<Object>|null|undefined} cart - Current cart rows; non-arrays are stored as [].
 */
export function persistCart(cart) {
  const normalizedCart = Array.isArray(cart) ? cart : [];
  const serialized = JSON.stringify(normalizedCart);
  const activeStorage = getActiveCartStorage();

  activeStorage.setItem('cart', serialized);
  if (activeStorage !== localStorage) {
    localStorage.setItem('cart', serialized);
  }

  logInfo(LOG_CONTEXT_CART_PERSISTENCE, 'persistCart', {
    itemCount: normalizedCart.length,
    activeStorage:
      activeStorage === localStorage ? 'localStorage' : 'sessionStorage',
  });
}

/**
 * Clear persisted cart in all relevant storages (used after successful quote requests).
 * Ensures session users do not reload stale cart rows from sessionStorage on refresh.
 */
export function clearPersistedCart() {
  const empty = JSON.stringify([]);
  const activeStorage = getActiveCartStorage();

  activeStorage.setItem('cart', empty);
  localStorage.setItem('cart', empty);
  if (sessionStorage.getItem('token')) {
    sessionStorage.setItem('cart', empty);
  }

  logInfo(LOG_CONTEXT_CART_PERSISTENCE, 'clearPersistedCart', {
    activeStorage:
      activeStorage === localStorage ? 'localStorage' : 'sessionStorage',
    clearedSessionStorage: Boolean(sessionStorage.getItem('token')),
  });
}

/**
 * This function creates a handler for the `beforeunload` window event that saves the application's state to storage.
 *
 * The created handler saves the current application state to either local storage or session storage. This includes the authentication token,
 * user details, notifications, supplier status, 'remember me' selection, and the shopping cart.
 * It then returns this handler function.
 *
 * @param {string} storageType - The type of web storage to use, either 'localStorage' or 'sessionStorage'.
 * @param {string} token - The current token of the authenticated user.
 * @param {Object} userDetails - The details of the authenticated user.
 * @param {Array} notifications - The current notifications for the user.
 * @param {boolean} isSupplier - A flag indicating if the current authenticated user is a supplier.
 * @param {boolean} rememberMe - A flag indicating if the 'Remember Me' option was selected during login.
 * @param {Array} cart - The current state of the shopping cart.
 * @param {boolean} notificationsSeen - A flag indicating if the notifications toast was seen
 * @returns {function} - The created handler function for the `beforeunload` window event.
 */
export const createBeforeUnloadHandler = (
  storageType,
  token,
  userDetails,
  notifications,
  isSupplier,
  rememberMe,
  cart,
  notificationsSeen
) => {
  // Return a function to handle beforeunload event
  return () => {
    // Choose the appropriate storage based on storageType
    let storage =
      storageType === 'localStorage' ? localStorage : sessionStorage;

    // Save the current state to the chosen storage
    storage.setItem('token', token);
    storage.setItem('userDetails', JSON.stringify(userDetails));
    storage.setItem('notifications', JSON.stringify(notifications));
    storage.setItem('isSupplier', String(isSupplier));
    storage.setItem('notificationsSeen', String(notificationsSeen));
    storage.setItem('rememberMe', String(rememberMe));
    storage.setItem('cart', JSON.stringify(cart));
  };
};

/**
 * Recursively deletes specified properties in an object.
 *
 * This function traverses into the given object and removes properties whose keys match with any key in the provided `propsToDelete` array.
 * This deep deletion happens recursively, so if a property value is an object itself, the function will recurse into it and remove matching properties there as well.
 *
 * @param {Object} obj - The object whose properties should be deleted.
 * @param {Array<string>} propsToDelete - An array of property keys (strings) which should be removed.
 * @returns {Object} - The modified object with the specified properties deleted. Note: Original object is modified and also returned
 */
export function deepDeleteProperties(obj, propsToDelete) {
  // Iterate over each property in the object
  for (const key in obj) {
    // Check if the property belongs to this object (not inherited)
    if (obj.hasOwnProperty(key)) {
      // Check if the current key is included in the list of properties to delete
      if (propsToDelete.includes(key)) {
        // Delete the property from the object
        delete obj[key];
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        // If the property value is an object itself, recurse into it
        deepDeleteProperties(obj[key], propsToDelete);
      }
    }
  }
  // Return the modified object
  return obj;
}

/**
 * Extract entities from a list of objects and update entity selection list.
 *
 * This function takes a list of objects and extracts entities specified by `entityType`.
 * Then it adds these entities to an existing entity list without duplication.
 *
 * Entities are either directly under `entityType` of input object or under a property named as plural form of `entityType`.
 * Adds unique entities into an array used for a selection list in the UI. If there are pre-existing entities, it does not overwrite them but appends the new unique entities.
 *
 * @param {Array<Object>} objList - The array of objects to extract entities from.
 * @param {Function} setEntityList - The setter function for the selection list. This function should take a new list and update the existing selection list state.
 * @param {string} entityType - The type of the entities to be extracted.
 */
export const extractEntitiesSelectList = (
  objList,
  setEntityList,
  entityType
) => {
  // Add 's' to entityType to get the plural form
  const pluralEntityType = entityType + 's';

  // Iterate over objList to find and extract entities
  const entityObjects = objList.flatMap(obj => {
    // Entities could be under the pluralEntityType or entityType property
    const entities = obj[pluralEntityType] || obj[entityType];
    // If no entity found return an empty array
    if (!entities) return [];
    // If entities are not already an array (i.e., a single entity), make it into a one-item array
    return Array.isArray(entities) ? entities : [entities];
  });

  // Use the setEntityList function to update the state of the entity list
  setEntityList(prevEntities => {
    // Create a Set for quickly looking up the IDs of existing entities
    const existingEntityIds = new Set(prevEntities.map(entity => entity?.id));
    // Create another Set to keep track of the IDs of new unique entities
    const uniqueEntityIds = new Set();

    // Filter the entityObjects to only include new unique entities
    const updatedEntitySelectList = entityObjects.filter(entity => {
      // If the entity exists and its ID is not in existingEntityIds or uniqueEntityIds,
      // add it to uniqueEntityIds and include the entity in updatedEntitySelectList
      if (
        entity &&
        !existingEntityIds.has(entity.id) &&
        !uniqueEntityIds.has(entity.id)
      ) {
        uniqueEntityIds.add(entity.id);
        return true;
      }
      // Exclude entity if it doesn't meet the criteria
      return false;
    });

    // Merge the entities already in state with the new unique entities
    // for the updated selection list
    return [...prevEntities, ...updatedEntitySelectList];
  });
};
/**
 * Filters objects by entity ID.
 *
 * This function takes a list of objects and filters them based on whether they contain an entity with a specified ID.
 * It handles both cases where the entities are stored in arrays and directly as object properties.
 * The entityType is used to determine which property in each object to check for entities.
 *
 * @param {string} entityId - The ID of the entity to filter by. Should be a string because it's converted to a number within the function.
 * @param {Array<Object>} objList - The list of objects to filter.
 * @param {Function} setFilteredList - The setter function for the filtered list. This function should take a new list and update the existing filtered list state.
 * @param {string} entityType - The type of the entities to filter by.
 */
export const filterObjectsByEntity = (
  entityId,
  objList,
  setFilteredList,
  entityType
) => {
  // Add 's' to entityType to get the plural form of entityType
  const pluralEntityType = entityType + 's';

  // Filter the list of objects
  const filteredObjList = objList.filter(obj => {
    // Entities could be found under the entityType or pluralEntityType property
    const entities = obj[pluralEntityType] || obj[entityType];
    // Exclude the obj if no entity is found
    if (!entities) return false;

    /* If entities are in an array, return true if at least one entity
    has an id that matches entityId. Otherwise, return whether the single entity's
    id is equal to entityId. */
    if (Array.isArray(entities)) {
      return entities.some(entity => entity?.id === parseInt(entityId, 10));
    } else {
      return entities?.id === parseInt(entityId, 10);
    }
  });

  // Update the filtered list by using the setFilteredList function
  setFilteredList(filteredObjList);
};

/**
 * Displays a toast notification with a given message, type, and position.
 *
 * This function is a wrapper for the toast function from react-toastify library. It sets some default options for the toast and makes it easier to call in app.
 * Toast is used to display brief notifications that appear at a specified position on the screen and disappear after a few seconds.
 * You can set the message, type (such as success, error, warn, info), and position for the toast.
 *
 * @param {string} message - The message to display in the toast.
 * @param {string} type - The type of toast. This sets the icon and styling for the toast. Can be one of the following: "info", "success", "warning", "error".
 * @param {string} position - The position of the toast on the screen. Can be one of the following: "top-right", "top-center", "top-left", "bottom-right", "bottom-center", "bottom-left".
 * @param {number or boolean} autoClose - Timing in milliseconds to close or false if manual close is required
 */
export const showToast = (message, type, position, autoClose) => {
  // Call the toast function with the message, type and position. Set some default options.
  toast[type](message, {
    position: position,
    autoClose: autoClose, // Toast will disappear after 3 seconds
    hideProgressBar: false, // Show the progress bar
    closeOnClick: true, // Close the toast when clicked
    pauseOnHover: true, // Pause the timer when mouse hovers over the toast
    draggable: true, // Make the toast draggable
    progress: undefined, // Do not set a custom progress bar value
    theme: 'light', // Use the colored theme for the toast
  });
};

/**
 * Check if the expiry date is within six months from the current date.
 *
 * @param {string} expiryDateString - The expiry date string in format "YYYY-MM-DD".
 * @returns {string|boolean} - Returns a string representation of the time difference in months, weeks, and days if
 * the expiry date is within six months, otherwise returns false.
 */
export const isExpiryInSixMonths = expiryDateString => {
  const expiryDate = new Date(expiryDateString);
  const currentDate = new Date();

  // Calculate the difference in months between the current date and the expiry date
  const monthDifference = differenceInMonths(expiryDate, currentDate);

  // set the default result
  let result = { expired: false };

  // If the expiry date is within six months, a string with time left until expiry and store it in the result object
  if (monthDifference <= 6) {
    const daysAfterMonths = differenceInDays(
      expiryDate,
      new Date(currentDate.setMonth(currentDate.getMonth() + monthDifference))
    );
    const weeks = Math.floor(daysAfterMonths / 7);
    const days = daysAfterMonths % 7;

    result.expired = true;
    result.timeTillExpiry = `(${monthDifference} M, ${weeks} W, ${days} D)`;
  }

  return result;
};

/**
 * Returns the current date in the format 'yyyy-mm-dd'.
 *
 * @returns {string} The current date formatted as 'yyyy-mm-dd'.
 */
export const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  let month = today.getMonth() + 1;
  let day = today.getDate();

  month = month < 10 ? `0${month}` : month;
  day = day < 10 ? `0${day}` : day;

  return `${year}-${month}-${day}`;
};

export function getCurrencySymbol(currencyCode) {
  // Return the symbol if it exists in the CURRENCY_SYMBOLS object, otherwise return the code itself
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
}

export const messageFormatDateAndTime = datetimeStr => {
  const now = new Date();
  const receivedDate = new Date(datetimeStr);
  const diffTime = Math.abs(now - receivedDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const time = receivedDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const date = receivedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const year = receivedDate.getFullYear();

  if (diffDays < 7) {
    return {
      receptionDate: `${date}, ${year}, ${time}`,
      timeDifference: `${diffDays} day${diffDays > 1 ? 's' : ''} ago`,
    };
  } else if (diffDays < 30) {
    const diffWeeks = Math.floor(diffDays / 7);
    return {
      receptionDate: `${date}, ${year}, ${time}`,
      timeDifference: `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`,
    };
  } else if (diffDays < 365) {
    const diffMonths = Math.floor(diffDays / 30);
    return {
      receptionDate: `${date}, ${year}, ${time}`,
      timeDifference: `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`,
    };
  } else {
    const diffYears = Math.floor(diffDays / 365);
    return {
      receptionDate: `${date}, ${year}, ${time}`,
      timeDifference: `${diffYears} year${diffYears > 1 ? 's' : ''} ago`,
    };
  }
};

/**
 * Calculates the percentage of a variable relative to a constant.
 *
 * @param {number} constant - The constant value.
 * @param {number} variable - The variable value.
 * @returns {number} The percentage value.
 */
export const calculatePercentage = (constant, variable) => {
  return (variable / constant) * 100;
};

/**
 * Updates the stock of a product in a list of products.
 * @param {Object} params - The parameters for updating the stock.
 * @param {Array} params.products - The array of products.
 * @param {string} params.productId - The ID of the product to update.
 * @param {string} [params.operation] - Operation to perform ('add' or 'subtract').
 * @param {number} [params.manualCreationValue] - Manual increment value for stock.
 * @param {number} [params.updatedAmount] - New total stock value.
 * @param {number} [params.effectiveStock] - Kit `effective_stock` from a derived stock response.
 * @param {object} [params.updatedStockItemObj] - Stock item object to update.
 * @param {object} [params.deletedStockItemObj] - Stock item object to remove.
 * @param {Array} [params.stockItemArray] - Array of stock items to set or append.
 * @param {boolean} [params.isAppendMode] - Whether to append (true) or replace (false) stock items.
 * @param {function} [params.updateStockItemsState] - Callback to update the stock items state.
 * @param {boolean} [params.editingStockItem] - Keep the current stock total (lot field edit).
 * @param {boolean} [params.previousInUse] - Lot was in use before this save.
 * @param {boolean} [params.nextInUse] - Lot is in use after this save.
 * @returns {Array} - The updated products array.
 */
export const updateProductStockInProductList = ({
  products,
  productId,
  operation,
  manualCreationValue,
  updatedAmount,
  effectiveStock,
  updatedStockItemObj,
  deletedStockItemObj,
  stockItemArray,
  isAppendMode = false,
  updateStockItemsState,
  editingStockItem,
  previousInUse,
  nextInUse,
}) => {
  if (!Array.isArray(products)) return products;
  const productIndex = products.findIndex(
    product => product && String(product.id) === String(productId)
  );
  let newStock = 0;

  if (productIndex !== -1) {
    const updatedProducts = products.slice();
    const product = updatedProducts[productIndex];
    const hadLots = Array.isArray(product.items) && product.items.length > 0;
    let nextItems = hadLots ? product.items.slice() : [];
    const currentStock = product.stock;

    // Determine new stock value based on input method
    if (editingStockItem) {
      newStock = currentStock;
    } else if (operation) {
      newStock =
        operation === 'add'
          ? Number(currentStock) + 1
          : Number(currentStock) - 1;
    } else if (manualCreationValue) {
      newStock = Number(currentStock) + Number(manualCreationValue);
    } else if (updatedAmount !== undefined) {
      newStock = Number(updatedAmount);
    }

    // A full lot array from the server replaces or extends stored lots.
    // A single-item edit on a row that has no lots must not invent `items: []`.
    let writeItems = false;
    if (Array.isArray(stockItemArray)) {
      nextItems = isAppendMode
        ? [...nextItems, ...stockItemArray]
        : [...stockItemArray];
      writeItems = true;

      if (updateStockItemsState) {
        updateStockItemsState({
          data: nextItems,
          replaceItems: !isAppendMode,
        });
      }
    }

    // Update a specific stock item if the row already stores lots
    if (updatedStockItemObj) {
      if (hadLots || writeItems) {
        nextItems = nextItems.map(item =>
          item && String(item.id) === String(updatedStockItemObj.id)
            ? updatedStockItemObj
            : item
        );
        writeItems = true;
      }

      if (updateStockItemsState) {
        updateStockItemsState({
          data: updatedStockItemObj,
          replaceSingleItem: true,
        });
      }
    }

    // Remove a specific stock item if the row already stores lots
    if (deletedStockItemObj) {
      if (hadLots || writeItems) {
        nextItems = nextItems.filter(
          item => !item || String(item.id) !== String(deletedStockItemObj.id)
        );
        writeItems = true;
      }

      if (updateStockItemsState) {
        updateStockItemsState({
          data: deletedStockItemObj,
          deleteItem: true,
        });
      }
    }

    const hasInUseTransition =
      previousInUse !== undefined && nextInUse !== undefined;
    const patch = buildListOpenedLotPatch(
      product,
      writeItems
        ? { items: nextItems }
        : { previousInUse, nextInUse }
    );

    const nextRow = {
      ...product,
      stock: newStock < 0 ? 0 : newStock,
      ...(effectiveStock !== undefined
        ? { effective_stock: effectiveStock }
        : {}),
    };

    if (patch.includeItems) {
      nextRow.items = patch.items;
      nextRow.opened_lot_count = patch.opened_lot_count;
    } else if (hasInUseTransition) {
      // Drop an empty lots array so Opened uses the adjusted field, not 0.
      if (Array.isArray(nextRow.items) && nextRow.items.length === 0) {
        delete nextRow.items;
      }
      nextRow.opened_lot_count = patch.opened_lot_count;
    }

    const previousOpened = product.opened_lot_count ?? null;
    const nextOpened = nextRow.opened_lot_count ?? null;
    if (previousOpened !== nextOpened) {
      logInfo('updateProductStockInProductList', 'opened lot count updated', {
        productId,
        previousOpenedLotCount: previousOpened,
        openedLotCount: nextOpened,
        hadLots,
      });
    }

    updatedProducts[productIndex] = nextRow;

    return updatedProducts;
  }

  return products;
};

/**
 * Updates the sub-stock of a stock item within a product.
 *
 * @param {Array} products - The array of products.
 * @param {string} productId - The ID of the product.
 * @param {string} stockItemId - The ID of the stock item.
 * @param {string} operation - The operation to perform on the sub-stock. Can be "add" or "subtract".
 * @param {number} updatedAmount - The updated sub-stock amount. Only used if `operation` is not provided.
 * @param {Object} updatedStockItemObj - The updated stock item object. If provided, `updatedAmount` is ignored.
 * @param {Function} updateStockItemsState - The callback function to update stock items state. Optional.
 *
 * @returns {Array} The updated array of products.
 */
export const updateStockItemSubStock = (
  products,
  productId,
  stockItemId,
  operation,
  updatedAmount,
  updatedStockItemObj,
  updateStockItemsState
) => {
  // Find the index of the product by its ID
  const productIndex = products.findIndex(product => product.id === productId);

  // If the product is found
  if (productIndex !== -1) {
    // Create a shallow copy of the products array
    const updatedProducts = products.slice();

    let updatedItems = updatedProducts[productIndex].items;

    // If updatedStockItemObj is provided, update the items array
    if (updatedStockItemObj) {
      updatedItems = updatedItems.map(item =>
        item.id === updatedStockItemObj.id ? updatedStockItemObj : item
      );

      // Optionally update the stock items state
      if (updateStockItemsState) {
        updateStockItemsState(operation);
      }
    } else {
      // Find the index of the stock item by its ID
      const stockItemIndex = updatedItems.findIndex(
        item => item.id === stockItemId
      );

      // If the stock item is found
      if (stockItemIndex !== -1) {
        // Create a shallow copy of the items array
        updatedItems = updatedItems.slice();

        // Determine the new sub-stock based on the operation
        let newSubStock = 0;
        const currentSubStock =
          updatedItems[stockItemIndex].item_sub_stock || 0;

        if (operation) {
          newSubStock =
            operation === 'add' ? currentSubStock + 1 : currentSubStock - 1;
        } else if (updatedAmount !== undefined && updatedAmount !== null) {
          newSubStock = updatedAmount;
        }

        // Ensure sub-stock doesn't go below zero
        newSubStock = newSubStock < 0 ? 0 : newSubStock;

        // Update the item_sub_stock in the item
        updatedItems[stockItemIndex] = {
          ...updatedItems[stockItemIndex],
          item_sub_stock: newSubStock,
        };

        // Optionally update the stock items state
        if (updateStockItemsState) {
          updateStockItemsState(operation);
        }
      }
    }

    // Update the items array in the product
    updatedProducts[productIndex] = {
      ...updatedProducts[productIndex],
      items: updatedItems,
    };
    return updatedProducts;
  }

  // If the product is not found, return the original products array
  return products;
};

export { mergeOrderAfterUpdate } from '../utils/mergeOrderAfterUpdate';

/**
 * A generic function used to replace/update an object in an array of objects.
 * @param {Array} objectsArray - Array of object objects.
 * @param {number} objectId - The id of the object to update.
 * @param {Object} newObject - The new object to replace the old one.
 * @returns {Array} - New array of objects with the specified object replaced by the new object.
 */
export const updateObjectInObjectArray = (
  objectsArray,
  objectId,
  newObject
) => {
  // Find the index of the product by its ID
  const objectIndex = objectsArray.findIndex(object => object.id === objectId);

  // If the product is found, replace it with the new product object
  if (objectIndex !== -1) {
    // Create a shallow copy of the products array
    const updatedObjects = objectsArray.slice();

    // Replace the product at the found index with the new product object
    updatedObjects[objectIndex] = newObject;

    updatedObjects.forEach(object => {
      // Check if the newObject is in the competing products array
      if (object?.competing_products) {
        object.competing_products.forEach(competingProduct => {
          if (competingProduct.id === newObject.id) {
            // Update the price, discount, and currency of the competing product
            competingProduct.price = newObject.price;
            competingProduct.discount = newObject.discount;
            competingProduct.currency = newObject.currency;
          }
        });
      }
    });

    return updatedObjects;
  }

  // If the product is not found, return the original products array
  return objectsArray;
};

/**
 * Format a decimal number for display.
 *
 * @param {number} num - The number to format.
 * @returns {number|string} - The formatted number or the original number if no formatting is needed.
 */
export const formatDecimalNumber = num => {
  // Handle null, undefined, or empty values
  if (num === null || num === undefined || num === '') {
    return '';
  }

  // Convert number to string for uniform processing
  let numStr = num.toString();
  // Check if the number has a decimal point
  if (numStr.includes('.')) {
    // Split the number into integer and decimal parts
    let [integerPart, decimalPart] = numStr.split('.');

    // Check the length of the decimal part
    if (decimalPart.length === 1 && decimalPart[0] === '0') {
      return parseInt(integerPart, 10);
    } else if (
      decimalPart.length > 1 &&
      decimalPart[1] === '0' &&
      decimalPart[0] === '0'
    ) {
      return parseInt(integerPart, 10);
    } else {
      // Otherwise, return the number with only two decimal places
      return parseFloat(numStr).toFixed(2);
    }
  }

  // Return the original number if no formatting is needed
  return num;
};

/**
 * Calculates the price after applying a discount.
 *
 * @param {string} numStr - The original price as a string.
 * @param {string} discountStr - The discount percentage as a string.
 * @returns {number} - The final price after applying the discount.
 */
export const calculatePriceAfterDiscount = (numStr, discountStr) => {
  const price = parseFloat(numStr);
  const discountPercentage = parseFloat(discountStr);

  const discountAmount = (price * discountPercentage) / 100;

  return price - discountAmount;
};

/**
 * Converts a date string from the format "yyyy-mm-dd" to "dd/mm/yyyy".
 *
 * @param {string} dateStr - The date string in the format "yyyy-mm-dd".
 * @returns {string} The converted date string in the format "dd/mm/yyyy".
 */
export const formatDateStr = dateStr => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Converts a date string from "dd/mm/yyyy" format to "yyyy-mm-dd" format.
 *
 * @param {string} dateStr - The date string in the format "dd/mm/yyyy".
 * @returns {string} The converted date string in the format "yyyy-mm-dd".
 */
export const parseDateStr = dateStr => {
  if (!dateStr) return '';
  const [day, month, year] = dateStr.split('/');
  if (
    day &&
    month &&
    year &&
    day.length === 2 &&
    month.length === 2 &&
    year.length === 4
  ) {
    return `${year}-${month}-${day}`;
  }
  return dateStr; // Return as-is if format doesn't match
};

/**
 * Formats a date value for display in dd/mm/yyyy format.
 * Handles both yyyy-mm-dd (from date inputs) and dd/mm/yyyy formats.
 *
 * @param {string} dateValue - The date value (can be yyyy-mm-dd or dd/mm/yyyy).
 * @returns {string} The date string in dd/mm/yyyy format.
 */
export const formatDateForInput = dateValue => {
  if (!dateValue) return '';
  // If it's already in yyyy-mm-dd format (from date input)
  if (dateValue.includes('-') && dateValue.split('-').length === 3) {
    return formatDateStr(dateValue);
  }
  // If it's already in dd/mm/yyyy format, return as-is
  if (dateValue.includes('/') && dateValue.split('/').length === 3) {
    return dateValue;
  }
  return dateValue;
};

/**
 * Retrieves the label corresponding to the given value from the EXPIRY_CATEGORIES array.
 * If the value does not match any category, it returns "Unknown".
 *
 * @param {number} value - The value to search for in the EXPIRY_CATEGORIES array.
 * @returns {string} - The label corresponding to the given value.
 */
export const getExpiryCategoryLabel = value => {
  return EXPIRY_CATEGORIES.find(cat => cat.value === value).label;
};

/**
 * Sorts the given array of products.
 *
 * Products are sorted based on their final prices,
 * calculated after applying any available discount.
 * Products with `null` price value are considered as the highest priced ones.
 *
 * @param {Object[]} products - The products to sort.
 * @returns {Object[]} The sorted array of products.
 */
export const sortProducts = products => {
  return products.sort((a, b) => {
    // If product a has 'null' price, consider it as higher priced
    if (a.price === null) return 1;

    // If product b has 'null' price, consider it as higher priced
    if (b.price === null) return -1;

    // Initialize final prices with the actual product prices
    let aFinalPrice = a.price;
    let bFinalPrice = b.price;

    // If product a has a discount available, recalculate its final price
    if (a.price && a.discount) {
      aFinalPrice = formatDecimalNumber(
        calculatePriceAfterDiscount(a.price, a.discount)
      );
    }

    // If product b has a discount available, recalculate its final price
    if (b.price && b.discount) {
      bFinalPrice = formatDecimalNumber(
        calculatePriceAfterDiscount(b.price, b.discount)
      );
    }

    // Return the difference of final prices of product a and b
    // This determines the sort order of products
    return aFinalPrice - bFinalPrice;
  });
};

/**
 * Function to convert a price in one currency to another currency using exchange rate
 *
 * @param {number} price - The price to be converted
 * @param {number} exchangeRate - The exchange rate between the two currencies
 * @returns {number} - The converted price in the target currency
 */
export const convertPriceToLis = (price, exchangeRate) => {
  return price * exchangeRate;
};

// Function to determine the final product price
/**
 * Calculates the final price of a product based on the given parameters.
 *
 * @param {Object} product - The product object. It should have the following properties:
 *   - price: The original price of the product.
 *   - discount: The discount percentage to be applied to the product price.
 *   - currency: The currency in which the product price is specified.
 * @param {Array} currencyRates - An array of currency rate objects. Each object should have the following properties:
 *   - symbol: The currency symbol.
 *   - exchange_rate: The exchange rate for the currency.
 * @param {boolean} [calculateDiscount=false] - Specifies whether to calculate the discounted price or not.
 * @param {string} [localCurrency="NIS"] - The local currency to convert the product price to.
 * @returns {number} The final price of the product.
 */
export const getFinalProductPrice = (
  product,
  currencyRates,
  calculateDiscount = false,
  localCurrency = 'NIS'
) => {
  // Find the exchange rate for the product's currency
  const baseProductExchangeRate =
    product.currency !== localCurrency
      ? parseFloat(
          currencyRates.find(
            currencyRate => product.currency === currencyRate.symbol
          )?.exchange_rate
        )
      : null;

  // Determine the product price after applying the discount if any
  const productPrice =
    calculateDiscount && product.price && product.discount
      ? formatDecimalNumber(
          calculatePriceAfterDiscount(product.price, product.discount)
        )
      : product.price;

  // Apply second ternary to check if the exchange rate is null or not, and then convert the price
  const finalProductPrice = baseProductExchangeRate
    ? formatDecimalNumber(
        convertPriceToLis(productPrice, baseProductExchangeRate)
      )
    : productPrice;

  return finalProductPrice;
};

/**
 * Calculates the price per unit.
 *
 * @param {number} finalPrice - The final price.
 * @param {number} unit - The number of units.
 * @returns {number} - The price per unit.
 */
export const calculatePricePerUnit = (finalPrice, unit) => {
  return formatDecimalNumber(finalPrice / unit);
};

/**
 * Calculates the price per sub unit based on the given final price, unit and sub unit.
 *
 * @param {number} finalPrice - The final price in the base currency.
 * @param {number} unit - The number of units.
 * @param {number} subUnit - The number of sub units per unit.
 * @returns {string} The formatted price per sub unit.
 */
export const calculatePricePerSubUnit = (finalPrice, unit, subUnit) => {
  return formatDecimalNumber(finalPrice / (unit * subUnit));
};

/**
 * Checks the status of items for use and updates product logs if necessary.
 * If the input data is an array, iterates over each item in the array and calls
 * the updateProductLogs function for items that are marked as 'in_use'.
 * If the input data is a single object, checks if the 'in_use' property is true
 * and calls the updateProductLogs function if it is true.
 *
 * @param {Array|Object} data - The data to be checked for use status.
 * @param {function} updateProductLogs - The function to update product logs for items in use.
 */
export const checkItemsForUseStatus = (data, updateProductLogs) => {
  if (Array.isArray(data)) {
    // Iterate over the array
    data.forEach(item => {
      if (item.in_use) {
        updateProductLogs(item);
      }
    });
  } else if (data) {
    // If it's a single object, check if in_use is true
    if (data.in_use) {
      updateProductLogs(data);
    }
  }
};

/**
 * Checks if a product is a kit based on its category.
 *
 * @param {Object} product - The product object to check.
 * @returns {boolean} - True if the product is a kit, false otherwise.
 */
export const isKitProduct = product => {
  return product && product.category === 'Kit';
};

/**
 * Gets the effective stock for a product.
 * For kit products, this returns the calculated effective stock.
 * For regular products, this returns the normal stock.
 *
 * @param {Object} product - The product object.
 * @returns {number|null} - The effective stock value.
 */
export const getEffectiveStock = product => {
  if (!product) return null;

  if (isKitProduct(product)) {
    // Completable kits from BOM; may be lower than `stock` when open kit lots block delete
    return product.effective_stock !== undefined
      ? product.effective_stock
      : product.stock;
  }

  return product.stock;
};

/**
 * Display numbers for a kit’s persisted package count vs completable kits.
 *
 * `stock` is complete kits on hand (StockItem count, derived from components).
 * `effective_stock` uses the same formula except leftover open kit lots that
 * blocked deletion can make `stock` higher until those units are closed.
 *
 * @param {Object} product
 * @returns {{
 *   stock: number|null,
 *   effectiveStock: number|null,
 *   hasOpenKitLeftover: boolean,
 * }}
 */
export const getKitStockDisplay = product => {
  if (!product) {
    return { stock: null, effectiveStock: null, hasOpenKitLeftover: false };
  }

  const toCount = value => {
    if (value === undefined || value === null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  if (!isKitProduct(product)) {
    const stock = toCount(product.stock);
    return { stock, effectiveStock: stock, hasOpenKitLeftover: false };
  }

  const stock = toCount(product.stock);
  const effectiveStock = toCount(product.effective_stock);
  const displayStock = stock !== null ? stock : effectiveStock;
  const displayEffective =
    effectiveStock !== null ? effectiveStock : displayStock;

  return {
    stock: displayStock,
    effectiveStock: displayEffective,
    hasOpenKitLeftover:
      displayStock !== null &&
      displayEffective !== null &&
      displayStock > displayEffective,
  };
};

/**
 * Gets a human-readable label for a project status.
 *
 * @param {string} status - The project status code (e.g., 'PLANNING', 'QUOTING').
 * @returns {string} The human-readable label for the status.
 */
export const getProjectStatusLabel = status => {
  const statusMap = {
    PLANNING: 'Planning',
    QUOTING: 'Quoting',
    ORDERING: 'Ordering',
    RECEIVING: 'Receiving',
    COMPLETED: 'Completed',
    ON_HOLD: 'On Hold',
  };
  return statusMap[status] || status;
};

/**
 * Gets a human-readable label for a project product status.
 *
 * @param {string} status - The product status code (e.g., 'NEEDS_QUOTE', 'QUOTED').
 * @returns {string} The human-readable label for the status.
 */
export const getProjectProductStatusLabel = status => {
  const statusMap = {
    NEEDS_QUOTE: 'Needs Quote',
    QUOTED: 'Quoted',
    ORDERED: 'Ordered',
    PARTIAL: 'Partial',
    RECEIVED: 'Received',
  };
  return statusMap[status] || status;
};

/**
 * Formats a progress percentage value for display.
 *
 * @param {number} percentage - The progress percentage (0-100).
 * @returns {string} The formatted percentage string.
 */
export const formatProgressPercentage = percentage => {
  if (percentage === null || percentage === undefined) {
    return '0%';
  }
  return `${Math.round(percentage)}%`;
};

/**
 * Formats owner information for display.
 *
 * @param {Object} owner - The owner object with id, username, email, first_name, last_name.
 * @returns {string} The formatted owner string (e.g., "John Doe (jdoe@example.com)") or "Unassigned" if null.
 */
export const formatOwnerDisplay = owner => {
  if (owner == null || owner === '') {
    return 'Unassigned';
  }

  if (typeof owner === 'number' || typeof owner === 'string') {
    return `User #${owner}`;
  }

  if (owner.first_name && owner.last_name) {
    return `${owner.first_name} ${owner.last_name} (${owner.email})`;
  }

  if (owner.username) {
    return `${owner.username} (${owner.email})`;
  }

  return owner.email || `User ${owner.id}`;
};

/**
 * Normalize an API id field that may be a pk or a nested `{ id }` object.
 *
 * @param {number|string|{ id?: number|string }|null|undefined} value
 * @returns {number|string|null}
 */
export const resolveEntityId = value => {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'object') {
    return value.id ?? null;
  }
  return value;
};

/**
 * Returns a display string for the user associated with an order.
 * Handles both nested user objects (first_name, last_name) and user as ID only (backend may not expand).
 *
 * @param {Object} order - Order object with optional user and/or quote.user (object or number).
 * @returns {string|null} Display string or null if no user.
 */
export const getAssociatedUserDisplay = order => {
  const user = order?.user ?? order?.quote?.user;
  if (user == null) {
    return null;
  }
  if (typeof user === 'number') {
    return `User #${user}`;
  }
  if (user.first_name != null && user.last_name != null) {
    return `${user.first_name} ${user.last_name}`.trim();
  }
  if (user.username) {
    return user.username;
  }
  return user.id != null ? `User #${user.id}` : null;
};
